import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Expenses from './Expenses';
import { useAuth } from '../contexts/AuthContext';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Slide,
  Snackbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccessTime,
  CheckCircle,
  Close,
  ExitToApp,
  Security,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const WARNING_AFTER_MS = 4 * 60 * 1000;
const DIALOG_AFTER_MS = 5 * 60 * 1000;
const COUNTDOWN_SECONDS = 30;

const palette = {
  navy: '#071A2F',
  navyLight: '#102A43',
  ivory: '#FBF7EF',
  champagne: '#D8B46A',
  champagneSoft: '#F3E7C6',
  ink: '#1C2430',
  muted: '#667085',
  danger: '#9F2A2A',
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const Dashboard = () => {
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showDialog, setShowDialog] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const activityTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const showDialogRef = useRef(false);
  const lastResetAtRef = useRef(Date.now());

  useEffect(() => {
    showDialogRef.current = showDialog;
  }, [showDialog]);

  const clearTimers = useCallback(() => {
    clearTimeout(activityTimeoutRef.current);
    clearTimeout(warningTimeoutRef.current);
    clearInterval(countdownIntervalRef.current);
  }, []);

  const countdownColor = useMemo(() => {
    if (countdown <= 10) return palette.danger;
    if (countdown <= 20) return '#A36B00';
    return palette.navy;
  }, [countdown]);

  const progress = (countdown / COUNTDOWN_SECONDS) * 100;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const beginCountdown = useCallback(() => {
    clearTimers();

    setShowWarning(false);
    setShowDialog(true);
    setCountdown(COUNTDOWN_SECONDS);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          logout();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, [clearTimers, logout]);

  const startInactivityTimer = useCallback(() => {
    clearTimers();

    setShowDialog(false);
    setShowWarning(false);
    setCountdown(COUNTDOWN_SECONDS);

    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
    }, WARNING_AFTER_MS);

    activityTimeoutRef.current = setTimeout(() => {
      beginCountdown();
    }, DIALOG_AFTER_MS);
  }, [beginCountdown, clearTimers]);

  useEffect(() => {
    const handleActivity = () => {
      if (showDialogRef.current) return;

      const now = Date.now();

      if (now - lastResetAtRef.current < 1000) return;

      lastResetAtRef.current = now;
      startInactivityTimer();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    startInactivityTimer();

    return () => {
      clearTimers();

      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [clearTimers, startInactivityTimer]);

  const handleStay = () => {
    startInactivityTimer();
  };

  const handleLogout = () => {
    clearTimers();
    setShowDialog(false);
    logout();
  };

  return (
    <>
      <Expenses />

      <Snackbar
        open={showWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: 2,
          mx: { xs: 1.5, sm: 0 },
        }}
      >
        <Alert
          icon={false}
          sx={{
            width: { xs: '100%', sm: 360 },
            borderRadius: 4,
            background: palette.ivory,
            color: palette.ink,
            border: `1px solid ${alpha(palette.champagne, 0.55)}`,
            boxShadow: '0 18px 45px rgba(7, 26, 47, 0.16)',
            px: 2,
            py: 1.5,
            alignItems: 'center',
          }}
          action={
            <Box display="flex" alignItems="center" gap={0.5}>
              <Button
                size="small"
                onClick={handleStay}
                sx={{
                  color: palette.navy,
                  fontWeight: 800,
                  textTransform: 'none',
                  borderRadius: 99,
                  px: 1.5,
                  '&:hover': {
                    background: alpha(palette.champagne, 0.18),
                  },
                }}
              >
                Continue
              </Button>

              <IconButton
                size="small"
                onClick={() => setShowWarning(false)}
                sx={{
                  color: palette.muted,
                  '&:hover': {
                    background: alpha(palette.navy, 0.06),
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Box>
            <Typography variant="body2" fontWeight={900}>
              Session notice
            </Typography>
            <Typography variant="caption" sx={{ color: palette.muted }}>
              You will be signed out soon due to inactivity.
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      <Dialog
        open={showDialog}
        TransitionComponent={Transition}
        keepMounted
        disableEscapeKeyDown
        fullScreen={isMobile}
        maxWidth="xs"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(7, 26, 47, 0.42)',
            backdropFilter: 'blur(5px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 5 },
            overflow: 'hidden',
            background: palette.ivory,
            boxShadow: '0 35px 90px rgba(7, 26, 47, 0.35)',
            border: { xs: 'none', sm: `1px solid ${alpha(palette.champagne, 0.35)}` },
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 3, sm: 4 },
            pt: { xs: 4, sm: 4 },
            pb: 3,
            background: `linear-gradient(135deg, ${palette.navy} 0%, ${palette.navyLight} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: -70,
              top: -70,
              width: 180,
              height: 180,
              borderRadius: '50%',
              background: alpha(palette.champagne, 0.14),
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              left: -50,
              bottom: -80,
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: alpha('#FFFFFF', 0.06),
            }}
          />

          <Box position="relative">
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: '18px',
                  display: 'grid',
                  placeItems: 'center',
                  background: alpha('#FFFFFF', 0.1),
                  border: `1px solid ${alpha(palette.champagne, 0.45)}`,
                  boxShadow: `inset 0 0 0 1px ${alpha('#FFFFFF', 0.06)}`,
                }}
              >
                <Security sx={{ fontSize: 28, color: palette.champagneSoft }} />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={900} letterSpacing="-0.02em">
                  Secure session
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.78, mt: 0.4 }}>
                  Elegant protection for your account.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <DialogContent
          sx={{
            px: { xs: 3, sm: 4 },
            py: { xs: 3.5, sm: 4 },
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Typography
              variant="body1"
              sx={{
                color: palette.ink,
                lineHeight: 1.75,
                mb: 3,
              }}
            >
              We noticed a period of inactivity. For your security, your session
              will end automatically unless you choose to continue.
            </Typography>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 4,
                background: '#FFFFFF',
                border: `1px solid ${alpha(palette.champagne, 0.38)}`,
                boxShadow: '0 14px 35px rgba(7, 26, 47, 0.07)',
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.2}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '12px',
                      display: 'grid',
                      placeItems: 'center',
                      background: alpha(palette.champagne, 0.16),
                    }}
                  >
                    <AccessTime sx={{ color: countdownColor, fontSize: 20 }} />
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color: palette.muted }}>
                      Time remaining
                    </Typography>
                    <Typography variant="body2" fontWeight={900} color={palette.ink}>
                      Before sign out
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="h5"
                  fontWeight={900}
                  sx={{
                    color: countdownColor,
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    letterSpacing: '-0.03em',
                  }}
                >
                  {formatTime(countdown)}
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  backgroundColor: alpha(palette.navy, 0.08),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 99,
                    background:
                      countdown <= 10
                        ? palette.danger
                        : `linear-gradient(90deg, ${palette.champagne} 0%, ${palette.navy} 100%)`,
                    transition: 'transform 0.45s ease',
                  },
                }}
              />
            </Box>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 2.2,
                color: palette.muted,
                textAlign: 'center',
              }}
            >
              Select “Stay logged in” to continue your session safely.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 3, sm: 4 },
            pb: { xs: 3.5, sm: 4 },
            pt: 0,
            gap: 1.5,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
          }}
        >
          <Button
            onClick={handleLogout}
            variant="text"
            size="large"
            startIcon={<ExitToApp />}
            fullWidth={isMobile}
            sx={{
              color: palette.muted,
              borderRadius: 99,
              py: 1.25,
              px: 3,
              fontWeight: 800,
              textTransform: 'none',
              '&:hover': {
                color: palette.danger,
                background: alpha(palette.danger, 0.06),
              },
            }}
          >
            Logout now
          </Button>

          <Button
            onClick={handleStay}
            variant="contained"
            size="large"
            startIcon={<CheckCircle />}
            autoFocus
            fullWidth={isMobile}
            sx={{
              borderRadius: 99,
              py: 1.25,
              px: 3.2,
              fontWeight: 900,
              textTransform: 'none',
              color: palette.navy,
              background: `linear-gradient(135deg, ${palette.champagneSoft} 0%, ${palette.champagne} 100%)`,
              boxShadow: `0 14px 30px ${alpha(palette.champagne, 0.35)}`,
              '&:hover': {
                background: `linear-gradient(135deg, #F8E9BD 0%, #CFA85A 100%)`,
                boxShadow: `0 18px 38px ${alpha(palette.champagne, 0.42)}`,
              },
            }}
          >
            Stay logged in
          </Button>
        </DialogActions>

        <AnimatePresence>
          {countdown <= 10 && (
            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.25, 0.75, 0.25] }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 1.4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                border: `2px solid ${alpha(palette.danger, 0.38)}`,
                borderRadius: { xs: 0, sm: 5 },
                pointerEvents: 'none',
              }}
            />
          )}
        </AnimatePresence>
      </Dialog>
    </>
  );
};

export default Dashboard;